import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import moment from "moment";

//Components
import {
    Grid,
    Typography,
    Container,
    Paper,
    Divider
} from "@mui/material";
import { Select } from "@gpstrackit/react-controls";
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
//Notification
import { NotificationManager } from "Components/Notifications";

//Helpers
import { getDateFormatted, getStatusOptions, getPMUser } from 'Core/data/Helpers';
import { HasPermission } from "Core/security"

//Views
import DeleteScheduledAction from 'Modules/scheduledActions/views/components/deleteScheduledAction';
import EditScheduledAction from 'Modules/scheduledActions/views/components/editScheduledAction';

//Actions
import { scheduledActionsOpenModal, createEditScheduledActionOpenModal, searchEntityRequest } from 'Redux/actions';

//Styles
import './index.scss';
const ScheduledActions = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const tableRef = useRef();

    //Redux States
    const editScheduledActionOpenModal = useSelector(state => state.editScheduledActionRedux.openModal);
    const deleteScheduledActionOpenModal = useSelector(state => state.deleteScheduledActionRedux.openModal);
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);
    const user = useSelector(state => state.securityRedux.user);
    const lang = useSelector(state => state.commonRedux.language);

    const options = useMemo(() => ([
        { id: "0", label: messages['common_not_executed'] },
        { id: "1", label: messages['common_executed'] }
    ]), [messages]);

    const [executed, setExecuted] = useState(options[0]);
    //Local states
    const [total, setTotal] = useState(0);
    const [columns, setColumns] = useState([
        {
            key: "entityName",
            field: "entityName",
            title: messages['scheduled_actions_entity_name'],
            width: 150,
            show: true,
            sort: true,
            resize: true,
            export: true,
            functionString: 'return row?.entityName',
            code: "scheduled_actions_entity_name",
            render: (item) => {
                return <span>{item?.entityName}</span>
            }
        },
        {
            key: "name",
            title: messages['scheduled_actions_name'],
            width: 200,
            show: true,
            resize: true,
            export: true,
            functionString: `
                if(row.entityName === 'Unit'){
                    return row?.unit?.label
                }
                if(row.entityName === 'Account'){
                    return row?.company?.name
                }
            `,
            code: "scheduled_actions_name",
            render: (item) => {
                return (
                    <>
                        {
                            item.entityName === "Unit" ? (
                                <Grid
                                    container
                                    alignItems="center"
                                    justifyContent="space-between"
                                    className="context-menu-container"
                                    wrap="nowrap"
                                >
                                    <Grid item>
                                        <NavLink to={`/app/devices/overview/${item?.unit?.id}`} style={{ color: 'black' }}>
                                            <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.unit?.label}</span>
                                        </NavLink>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Grid
                                    container
                                    alignItems="center"
                                    justifyContent="space-between"
                                    className="context-menu-container"
                                    wrap="nowrap"
                                >
                                    <Grid item>
                                        <NavLink to={`/app/accounts/overview/${item?.company?.id}`} style={{ color: 'black' }}>
                                            <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.company?.name}</span>
                                        </NavLink>
                                    </Grid>
                                </Grid>
                            )
                        }
                    </>
                )
            }
        },
        {
            key: "action",
            field: "data",
            title: messages['scheduled_actions_action'],
            width: 250,
            show: true,
            resize: true,
            export: true,
            code: "scheduled_actions_action",
            functionString: `
                let label = '';
                let status = ''
                try {
                    let data = JSON.parse(row.data)
                    if (data.newStatusId) {
                        label = "${messages['change_status_title']}"
                        const statusOptions = [
                            {
                                id: 1,
                                label: "${messages['active']}"
                            },
                            {
                                id: 4,
                                label: "${messages['cancelled']}"
                            },
                            {
                                id: 3,
                                label: "${messages['involuntary_suspension']}"
                            },
                            {
                                id: 2,
                                label: "${messages['voluntary_suspension']}"
                            },
                            {
                                id: 6,
                                label: "RMA"
                            }
                        ]

                        let currentStatus = statusOptions.find((item) => item.id === data.newStatusId)
                        if (currentStatus.id) {
                            status = currentStatus.label
                        }
                    }
                }
                catch(error){}

                return label + " - " + status
            `,
            render: (item) => {
                let label = '';
                let status = ''
                try {
                    let data = JSON.parse(item.data)
                    if (data.newStatusId) {
                        label = messages['change_status_title']
                        let currentStatus = getStatusOptions(messages).find((item) => item.id === data.newStatusId)
                        if (currentStatus.id) {
                            status = currentStatus.label
                        }
                    }

                } catch (error) { }
                return <span>{`${label} - ${status}`}</span>
            }
        },
        {
            key: "scheduleOn",
            field: "scheduleOn",
            title: messages['scheduled_actions_schedule_on'],
            width: 200,
            show: true,
            sort: true,
            resize: true,
            export: true,
            code: "scheduled_actions_schedule_on",
            functionString: 'return row?.scheduleOn ? moment(row?.scheduleOn).utc().format("MMM DD YYYY") : ""',
            render: (item) => {
                return <span>{item?.scheduleOn ? moment(item?.scheduleOn).utc().format('MMM DD YYYY') : ""}</span>
            }
        },
        {
            key: "executed",
            field: "executed",
            title: messages['scheduled_actions_executed'],
            width: 200,
            show: true,
            sort: true,
            resize: true,
            export: true,
            code: "scheduled_actions_executed",
            functionString: `return row.executed ? "${messages['common_yes']}" : "${messages['common_no']}"`,
            render: (item) => {
                return <span>{item.executed ? messages['common_yes'] : messages['common_no']}</span>
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['scheduled_actions_created_on'],
            width: 200,
            show: true,
            sort: true,
            resize: true,
            export: true,
            code: "scheduled_actions_created_on",
            functionString: 'return row?.createdOn && (new Date(`${row?.createdOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime", null, "UTC")}</span>
            }
        },
        {
            key: "createdBy",
            field: "createdBy",
            title: messages['scheduled_actions_created_by'],
            width: 200,
            show: true,
            resize: true,
            export: true,
            code: "scheduled_actions_created_by",
            render: (item) => {
                let label = item?.createdBy ? getPMUser(item?.createdBy, pmUsers) : ""
                return <span>{label}</span>
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['scheduled_actions_tab_updated_on'],
            width: 200,
            show: true,
            resize: true,
            export: true,
            code: "scheduled_actions_tab_updated_on",
            functionString: 'return row?.updatedOn && (new Date(`${row?.updatedOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            render: (item) => {
                return <span>{item?.updatedOn && getDateFormatted(item?.updatedOn, "date", "datetime", null, "UTC")}</span>
            }
        },
        {
            key: "updatedBy",
            field: "updatedBy",
            title: messages['scheduled_actions_tab_updated_by'],
            width: 200,
            show: true,
            resize: true,
            export: true,
            code: "scheduled_actions_tab_updated_by",
            render: (item) => {
                let label = item?.updatedBy ? getPMUser(item?.updatedBy, pmUsers) : ""
                return <span>{label}</span>
            }
        },
    ])

    const executedHandler = useCallback((exec) => {
        //save preferences
        let currentNewConditions = tableRef?.current?.conditions;
        currentNewConditions = currentNewConditions?.filter((item) => item?.fields[0] !== 'executed');
        if (exec)
            currentNewConditions.push({ fields: ["executed"], terms: [exec.id], conditionOperator: "AND" })
        tableRef?.current?.getData({ newConditions: currentNewConditions });
        setExecuted(exec);
    }, []);

    // =========================================================================
    // CALLBACK HANDLERS
    // =========================================================================

    const handleChangeTotal = useCallback((total) => setTotal(total), []);

    const handleChangeColumns = useCallback((items) => setColumns(items), []);

    const handleClickMenu = useCallback((event, item) => {
        if (event === messages['delete']) {
            if (HasPermission(user, "schact", "d")) {
                if (item.executed) {
                    NotificationManager.warning(
                        messages['scheduled_actions_tab_delete_is_executed'],
                        messages['delete_scheduled_action_title'],
                        5000,
                    );
                } else {
                    dispatch(scheduledActionsOpenModal(item.id))
                }
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['delete_scheduled_action_title'],
                    5000,
                );
            }
        }
        if (event === messages['edit']) {
            if (HasPermission(user, "schact", "u")) {
                if (item.executed) {
                    NotificationManager.warning(
                        messages['scheduled_actions_tab_edit_is_executed'],
                        messages['edit_scheduled_action_title'],
                        5000,
                    );
                } else {
                    dispatch(createEditScheduledActionOpenModal(true, item.id))
                }
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['edit_scheduled_action_title'],
                    5000,
                );
            }
        }
    }, [messages, user, dispatch]);

    const menuOptions = useCallback(() => [messages['edit'], messages['delete']], [messages]);

    const handleChangeConditions = useCallback((tags, conditions, total, newColumns, rows, sort, key, fromPreferences, restoreOnly) => {
        // restoreOnly: data already persisted in redux, just sync UI states — skip getData
        if (restoreOnly) return;

        let currentConditions = {}
        let currentTags = {}

        let currentExecuted = executed?.id
        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) {
                tableRef?.current?.setTags(currentTags);
            }

            let validateExecuted = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "executed");

            if (validateExecuted?.fields) {
                let currentOption = options.find((item) => item.id === validateExecuted?.terms)
                if (currentOption) {
                    currentExecuted = currentOption?.id
                    setExecuted(currentOption)
                }
            }
        }

        currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'entityName' && item?.fields[0] !== 'executed');

        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length > 0) {
            //New Items
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: tagsById, conditionOperator: "OR" });
            }
        }

        let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel
                .map((item) => item['entityName company { name } unit { label }'] || item?.entityName || item?.label || item?.id)
                .filter((item) => typeof item === "string" && item.trim().length > 0)
                .map((item) => item.trim())
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: ["entityName", "unit_label", "company_name"], terms: currentLabels, exact: false, tags: tagsByLabel, conditionOperator: "OR" })
            }
        }
        executed && currentConditions && currentConditions.push({ fields: ["executed"], terms: [currentExecuted || executed?.id || "0"], conditionOperator: "AND" })

        if (!conditions && tags) tableRef?.current?.getData({ newConditions: currentConditions });
        else tableRef?.current?.getData({ newConditions: currentConditions, newRows: rows, newSort: sort });
    }, [executed, options]);

    // Custom label for search options
    const openSearchCustomOptions = useCallback((item) => {
        // Typed tags have no systemAlert data - return label as-is
        if (!item.data) {
            return item.label || item.id;
        }
        // Selected from dropdown - apply custom formatting
        const actionEntity = item.entityName;
        const entityName = actionEntity == "Unit" ? item?.unit?.label || '' : item?.company?.name || '';
        const actionName = JSON.parse(item.data)?.newStatusName || '';

        return `#${item.id}${entityName ? ` | ${entityName}` : ''}${actionName ? ` - ${actionName}` : ''}`;

    }, []);

    const tableOptions = useMemo(() => [
        {
            id: "edit-scheduled-action",
            label: messages['edit'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "schact", "u"),
            action: (items) => {
                let isExecuted = items.some((item) => item.executed)
                if (isExecuted) {
                    NotificationManager.warning(
                        messages['scheduled_actions_tab_edit_is_executed'],
                        messages['edit_scheduled_action_title'],
                        5000,
                    );
                } else {
                    let currentItem = items.find(item => item.id);
                    dispatch(createEditScheduledActionOpenModal(true, currentItem.id))
                }
            }
        },
        {
            id: "delete-scheduled-action",
            label: messages['delete'],
            multiple: true,
            show: true,
            disabled: !HasPermission(user, "schact", "d"),
            action: (items) => {
                let isExecuted = items.some((item) => item.executed)
                if (isExecuted) {
                    NotificationManager.warning(
                        messages['scheduled_actions_tab_delete_is_executed'],
                        messages['delete_scheduled_action_title'],
                        5000,
                    );
                } else {
                    let currentItem = items.map(item => item.id);
                    dispatch(scheduledActionsOpenModal(currentItem))
                }
            }
        }
    ], [messages, user, dispatch]);

    const extraFilters = useCallback(() => {
        return (
            <div className="scheduled-actions-select" style={{ marginRight: 5, width: 165 }}>
                <Select
                    id="executed"
                    placeholder={messages['type']}
                    options={options}
                    value={executed}
                    textField="label"
                    valueField="id"
                    onChange={(items = []) => executedHandler(items)}
                    isClearable
                />
            </div>
        )
    }, [messages, options, executed, executedHandler]);

    // =========================================================================
    // CONFIG OBJECTS
    // =========================================================================

    const graphqlConfig = useMemo(() => ({
        function: "searchScheduledActions",
        sortType: "ScheduledActionSortInput",
        conditionsType: "ScheduledActionSearchInput",
        customQuery: "id company { id name } unit { id label } transactionId",
        dynamicQuery: true
    }), []);

    const exportConfig = useMemo(() => ({
        fileName: "scheduledactions"
    }), []);

    const searchConfig = useMemo(() => ({
        conditions: ["entityName", "unit_label", "company_name"],
        fields: ["entityName", "unit_label", "company_name"],
        optionValue: "id",
        optionLabel: "entityName",
        extraField: "entityName data company { name } unit { label }",
        openCustomOptions: openSearchCustomOptions
    }), [openSearchCustomOptions]);

    const uiConfig = useMemo(() => ({
        hideSplit: true,
        hideFilters: true,
        topHeight: 210
    }), []);

    useEffect(() => {
        (pmUsers?.length === 0) && dispatch(searchEntityRequest({ key: "pmUsers" }));
    }, [pmUsers]);

    useEffect(() => {
        if (pmUsers.length > 0) {
            let currentColumns = columns.map((item) => ({
                ...item,
                title: messages[item.code],
                functionString: item.key === "createdBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
                ` : item.key === "updatedBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.updatedBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.updatedBy
                ` : item.key === "executed" ? `
                    return row.executed ? "${messages['common_yes']}" : "${messages['common_no']}"
                ` : item.key === "action" ? `
                    let label = '';
                    let status = ''
                    try {
                        let data = JSON.parse(row.data)
                        if (data.newStatusId) {
                            label = "${messages['change_status_title']}"
                            const statusOptions = [
                                {
                                    id: 1,
                                    label: "${messages['active']}"
                                },
                                {
                                    id: 4,
                                    label: "${messages['cancelled']}"
                                },
                                {
                                    id: 3,
                                    label: "${messages['involuntary_suspension']}"
                                },
                                {
                                    id: 2,
                                    label: "${messages['voluntary_suspension']}"
                                },
                                {
                                    id: 6,
                                    label: "RMA"
                                }
                            ]

                            let currentStatus = statusOptions.find((item) => item.id === data.newStatusId)
                            if (currentStatus.id) {
                                status = currentStatus.label
                            }
                        }
                    }
                    catch(error){}
                    return label + " - " + status
                ` : item.functionString,
                render: item.key === "createdBy" ? (item) => {
                    let label = item?.createdBy ? getPMUser(item?.createdBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.key === "updatedBy" ? (item) => {
                    let label = item?.updatedBy ? getPMUser(item?.updatedBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.key === "executed" ? (item) => {
                    return <span>{item.executed ? messages['common_yes'] : messages['common_no']}</span>
                } : item.key === "action" ? (item) => {
                    let label = '';
                    let status = '';
                    try {
                        let data = JSON.parse(item.data)
                        if (data.newStatusId) {
                            label = messages['change_status_title']
                            let currentStatus = getStatusOptions(messages).find((item) => item.id === data.newStatusId)
                            if (currentStatus.id) {
                                status = currentStatus.label
                            }
                        }
                    } catch (error) { }
                    return <span>{`${label} - ${status}`}</span>
                } : item.render,
            }))
            setColumns([...currentColumns])

            if (executed) {
                let currentState = options.find((item) => item.id === executed.id)
                if (currentState.id) {
                    setExecuted(currentState)
                }
            }
        }
    }, [pmUsers, lang])

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={6}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['scheduled_actions_title']} {/* ({total}) */}
                        </Typography>
                    </Grid>
                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataViewInfiniteScroll
                        ref={tableRef}
                        infiniteScroll
                        clearOnUnmount={false}
                        entity="scheduled-actions"
                        name="pm-schedules-actins-main-v2"
                        columns={columns}
                        asyncSelect
                        tableButtons={["export", 'orderColumn']}
                        onChangeTotal={handleChangeTotal}
                        onChangeColumns={handleChangeColumns}
                        onChangeConditions={handleChangeConditions}
                        menuOptions={menuOptions}
                        onClickMenu={handleClickMenu}
                        options={tableOptions}
                        extraFilters={extraFilters}
                        graphqlConfig={graphqlConfig}
                        exportConfig={exportConfig}
                        searchConfig={searchConfig}
                        uiConfig={uiConfig}
                    />
                </Grid>
            </Paper>
            {deleteScheduledActionOpenModal && <DeleteScheduledAction tableRef={tableRef} />}
            {editScheduledActionOpenModal && <EditScheduledAction tableRef={tableRef} />}
        </Container>
    )
}
export default ScheduledActions;